import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { X } from 'lucide-react';
import AnimatedText from './AnimatedText';

// Two coordinate sets — mobile keeps cards inside viewport by using narrower
// left offsets so a ~85vw card never overflows.
const desktopCards = [
  { text: 'Influencer collaborations that bring views but no results', top: 90, left: '55%', rotate: -3 },
  { text: 'Posting consistently but getting low engagement', top: 130, left: '5%', rotate: 4 },
  { text: 'Content looks "nice" but doesn\'t perform', top: 170, left: '40%', rotate: -2 },
  { text: 'Campaigns feel forced and salesy', top: 210, left: '8%', rotate: 3 },
  { text: 'No clear content direction or strategy', top: 250, left: '55%', rotate: -4 },
];

const mobileCards = [
  { text: 'Influencer collaborations that bring views but no results', top: 88, left: '8%', rotate: -3 },
  { text: 'Posting consistently but getting low engagement', top: 128, left: '6%', rotate: 4 },
  { text: 'Content looks "nice" but doesn\'t perform', top: 168, left: '9%', rotate: -2 },
  { text: 'Campaigns feel forced and salesy', top: 208, left: '5%', rotate: 3 },
  { text: 'No clear content direction or strategy', top: 248, left: '7%', rotate: -4 },
];

const SECTION_HEIGHT_VH_DESKTOP = 320;
const SECTION_HEIGHT_VH_MOBILE = 340;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const Card = ({ card }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, scale: 0.92 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.55, ease: [0.2, 0.65, 0.3, 0.9] }}
    style={{
      top: `${card.top}vh`,
      left: card.left,
      rotate: card.rotate,
    }}
    className="absolute w-[calc(100vw-3.5rem)] max-w-[320px] sm:w-[300px] lg:w-[340px] bg-[#44403C] text-[#F4F4F3] rounded-2xl p-5 sm:p-6 shadow-2xl"
  >
    <div className="w-9 h-9 rounded-full bg-[#AA94FF] flex items-center justify-center mb-4">
      <X className="w-4 h-4 text-[#1C1917]" strokeWidth={3} />
    </div>
    <p
      className="text-base sm:text-xl font-bold leading-[1.25] tracking-[-0.01em]"
      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
    >
      {card.text}
    </p>
  </motion.div>
);

const Problem = () => {
  const sectionRef = useRef(null);
  const isMobile = useIsMobile();
  const cards = isMobile ? mobileCards : desktopCards;
  const sectionHeightVh = isMobile ? SECTION_HEIGHT_VH_MOBILE : SECTION_HEIGHT_VH_DESKTOP;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const bgColor = useTransform(
    scrollYProgress,
    [0, 0.5, 0.92, 1],
    ['#1C1917', '#1C1917', '#1C1917', '#F4F4F3']
  );

  return (
    <motion.section
      ref={sectionRef}
      data-testid="problem-section"
      style={{
        backgroundColor: bgColor,
        height: `${sectionHeightVh}vh`,
      }}
      className="relative"
    >
      {/* Sticky heading — pinned centred while user scrolls entire section */}
      <div className="sticky top-0 h-screen flex items-center justify-center px-6 sm:px-8 lg:px-12 pointer-events-none z-0">
        <div className="max-w-5xl w-full text-center">
          <AnimatedText
            as="h2"
            className="text-[9vw] sm:text-7xl lg:text-8xl font-black tracking-[-0.03em] leading-[0.95] text-[#F4F4F3]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            y={60}
            stagger={0.06}
            once
          >
            Social media feels harder than it should be.
          </AnimatedText>
        </div>
      </div>

      {/* Cards layer — overflow-hidden here (not on section) so sticky still works */}
      <div
        className="absolute inset-x-0 top-0 z-10 pointer-events-none max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 overflow-hidden"
        style={{ height: `${sectionHeightVh}vh` }}
      >
        {cards.map((card, i) => (
          <Card key={i} card={card} />
        ))}
      </div>
    </motion.section>
  );
};

export default Problem;
